'use client';

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/form/Checkbox';
import { PrivacyPolicyModalContainer } from '@/components/modals/PrivacyPolicyModal';
import { TermsAndConditionsModalContainer } from '@/components/modals/TermsAndConditionsModal';
import { getMinAgeByCountry } from '@/helpers/conf';
import { checkboxRequired } from '@/helpers/validators';
import { useCommonStore } from '@/store';

import { ScPromoCheckboxesContainer } from '../styled';
import { ScLink } from '@/components/form/groups/styled';

export interface RegistrationFormCheckboxListProps {
  formId?: string;
  showPromosCheckboxes: boolean;
  openInnerModalsInPortal?: boolean;
  hideAcceptPartnerPromotions?: boolean;
}

export const RegistrationFormCheckboxList = ({
  formId,
  showPromosCheckboxes,
  openInnerModalsInPortal,
  hideAcceptPartnerPromotions,
}: RegistrationFormCheckboxListProps) => {
  const countryCode = useCommonStore.use.countryCode();
  const { t } = useTranslation('common');

  const [isOpenPrivacy, setIsOpenPrivacy] = useState(false);
  const [isOpenTerms, setIsOpenTerms] = useState(false);

  const toggleTerms = (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e?.preventDefault();
    setIsOpenTerms((s) => !s);
  };

  const togglePrivacy = (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e?.preventDefault();
    setIsOpenPrivacy((s) => !s);
  };

  const age = getMinAgeByCountry(countryCode);

  return (
    <>
      <Checkbox
        label={
          <Trans
            ns="common"
            i18nKey="auth.registration.form.acceptTermsConditions"
            components={{
              1: <ScLink onClick={toggleTerms} />,
              2: <ScLink onClick={togglePrivacy} />,
            }}
            values={{ age }}
          />
        }
        name="termsAcceptanceAgeAcceptance"
        validate={checkboxRequired}
        formId={formId}
      />
      <Checkbox
        label={t('common:auth.registration.form.offersCheckboxes.refuseOffers')}
        name="receivePromosMain"
        formId={formId}
        tooltip={t('common:auth.registration.form.offersCheckboxes.tooltip')}
      />
      <ScPromoCheckboxesContainer isOpen={showPromosCheckboxes}>
        <Checkbox
          label={t(
            'common:auth.registration.form.offersCheckboxes.refuseEmailOffers'
          )}
          name="receivePromos"
          formId={formId}
        />
        <Checkbox
          label={t(
            'common:auth.registration.form.offersCheckboxes.refuseSmsOffers'
          )}
          name="receiveSmsPromos"
          formId={formId}
        />
        <Checkbox
          label={t(
            'common:auth.registration.form.offersCheckboxes.refusePhoneOffers'
          )}
          name="receivePromosViaPhoneCalls"
          formId={formId}
        />
      </ScPromoCheckboxesContainer>
      {!hideAcceptPartnerPromotions && (
        <Checkbox
          label={t('common:auth.registration.form.acceptPartnerPromotions')}
          name="agreedToPartnerPromotions"
          formId={formId}
        />
      )}
      {isOpenPrivacy && (
        <PrivacyPolicyModalContainer
          onClose={togglePrivacy}
          isPortal={openInnerModalsInPortal}
        />
      )}
      {isOpenTerms && (
        <TermsAndConditionsModalContainer
          onClose={toggleTerms}
          isPortal={openInnerModalsInPortal}
        />
      )}
    </>
  );
};
